import React from 'react';
import { ArrowLeft, Lightbulb, Zap, FileText, Image, Copy, Download, Share2, CheckCircle2, Video, Sparkles } from 'lucide-react';
import { Button } from './ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';
import { Badge } from './ui/Badge';
import { cn } from '../lib/utils';
export interface SwipefileReference {
  id: string;
  title: string;
  thumbnail: string;
  topic: string;
  usedFor: string[]; // e.g., ["Hook inspiration", "Visual style", "Pacing"]
}
export interface ConceptDetailsData {
  id: string;
  title: string;
  thumbnail: string;
  category: string;
  format: string;
  status: 'draft' | 'ready' | 'published';
  dateCreated: string;
  // Context
  originalIdea: {
    title: string;
    description: string;
    tags: string[];
  };
  // Swipefile References - EMPHASIZED
  swipefileReferences: SwipefileReference[];
  // Hook
  selectedHook: {
    text: string;
    tone: string;
  };
  // Script
  script: {
    intro: string;
    body: string[];
    outro: string;
    duration: string;
  };
  // Visual Reference
  visualReference: {
    style: string;
    colorPalette: string[];
    shots: Array<{
      timestamp: string;
      description: string;
      notes: string;
      inspiredBy?: string; // Reference to swipefile video
    }>;
  };
}
interface ConceptDetailsProps {
  concept: ConceptDetailsData;
  onBack: () => void;
}
export function ConceptDetails({
  concept,
  onBack
}: ConceptDetailsProps) {
  const statusConfig = {
    draft: {
      color: 'bg-gray-100 text-gray-700',
      label: 'Draft'
    },
    ready: {
      color: 'bg-green-100 text-green-700',
      label: 'Ready'
    },
    published: {
      color: 'bg-blue-100 text-blue-700',
      label: 'Published'
    }
  };
  return <div className="flex-1 h-full flex flex-col bg-gray-50/50 overflow-auto" data-id="element-233">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b px-8 py-6" data-id="element-234">
        <div className="flex items-center gap-4 mb-4" data-id="element-235">
          <Button variant="ghost" size="icon" onClick={onBack} className="hover:bg-gray-100" data-id="element-236">
            <ArrowLeft className="h-5 w-5" data-id="element-237" />
          </Button>
          <div className="flex-1" data-id="element-238">
            <div className="flex items-center gap-3 mb-2" data-id="element-239">
              <h1 className="text-3xl font-bold text-gray-900" data-id="element-240">
                {concept.title}
              </h1>
              <Badge className={`${statusConfig[concept.status].color} border-none font-medium`} data-id="element-241">
                <CheckCircle2 className="h-3 w-3 mr-1" data-id="element-242" />
                {statusConfig[concept.status].label}
              </Badge>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-500" data-id="element-243">
              <Badge variant="outline" className="border-gray-200" data-id="element-244">
                {concept.category}
              </Badge>
              <span data-id="element-245">•</span>
              <span data-id="element-246">{concept.format}</span>
              <span data-id="element-247">•</span>
              <span data-id="element-248">{concept.dateCreated}</span>
              <span data-id="element-249">•</span>
              <span className="flex items-center gap-1.5 text-[#139191] font-medium" data-id="element-250">
                <Video className="h-3.5 w-3.5" data-id="element-251" />
                {concept.swipefileReferences.length} Swipefile References Used
              </span>
            </div>
          </div>
          <div className="flex gap-2" data-id="element-252">
            <Button variant="outline" className="gap-2 border-gray-200" data-id="element-253">
              <Share2 className="h-4 w-4" data-id="element-254" />
              Share
            </Button>
            <Button variant="outline" className="gap-2 border-gray-200" data-id="element-255">
              <Download className="h-4 w-4" data-id="element-256" />
              Export
            </Button>
            <Button className="bg-[#139191] hover:bg-[#139191]/90 text-white gap-2" data-id="element-257">
              <CheckCircle2 className="h-4 w-4" data-id="element-258" />
              Mark as Ready
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-8" data-id="element-259">
        <div className="max-w-7xl mx-auto space-y-8" data-id="element-260">
          {/* Visual Reference Hero */}
          <Card className="overflow-hidden border-gray-200 shadow-lg" data-id="element-261">
            <div className="relative aspect-video bg-gray-100" data-id="element-262">
              <img src={concept.thumbnail} alt={concept.title} className="w-full h-full object-cover" data-id="element-263" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" data-id="element-264" />
              <div className="absolute bottom-6 left-6 right-6" data-id="element-265">
                <h2 className="text-white text-2xl font-bold mb-2" data-id="element-266">
                  Visual Reference
                </h2>
                <p className="text-white/90 text-sm" data-id="element-267">
                  AI-generated visual style and mood board
                </p>
              </div>
            </div>
          </Card>

          {/* SWIPEFILE REFERENCES - PROMINENT SECTION */}
          <Card className="border-[#139191]/30 shadow-lg bg-gradient-to-br from-[#139191]/5 to-transparent" data-id="element-268">
            <CardHeader className="pb-4" data-id="element-269">
              <div className="flex items-center gap-3" data-id="element-270">
                <div className="h-12 w-12 rounded-lg bg-[#139191] flex items-center justify-center" data-id="element-271">
                  <Video className="h-6 w-6 text-white" data-id="element-272" />
                </div>
                <div data-id="element-273">
                  <CardTitle className="text-2xl text-gray-900" data-id="element-274">
                    Swipefile References
                  </CardTitle>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-275">
                    AI analyzed these {concept.swipefileReferences.length}{' '}
                    videos from your swipefile to create this concept
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent data-id="element-276">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-277">
                {concept.swipefileReferences.map(ref => <Card key={ref.id} className="overflow-hidden border-gray-200 hover:shadow-md transition-shadow bg-white" data-id="element-278">
                    <div className="relative aspect-video bg-gray-100" data-id="element-279">
                      <img src={ref.thumbnail} alt={ref.title} className="w-full h-full object-cover" data-id="element-280" />
                      <Badge className="absolute top-2 left-2 bg-[#139191] text-white border-none text-xs" data-id="element-281">
                        {ref.topic}
                      </Badge>
                    </div>
                    <CardContent className="p-4" data-id="element-282">
                      <h4 className="font-semibold text-sm text-gray-900 mb-3 line-clamp-2" data-id="element-283">
                        {ref.title}
                      </h4>
                      <div className="space-y-1.5" data-id="element-284">
                        <p className="text-xs font-semibold text-gray-700 mb-1" data-id="element-285">
                          Used for:
                        </p>
                        {ref.usedFor.map((usage, idx) => <div key={idx} className="flex items-center gap-2" data-id="element-286">
                            <Sparkles className="h-3 w-3 text-[#139191] flex-shrink-0" data-id="element-287" />
                            <span className="text-xs text-gray-600" data-id="element-288">
                              {usage}
                            </span>
                          </div>)}
                      </div>
                    </CardContent>
                  </Card>)}
              </div>

              {/* AI Analysis Summary */}
              <div className="mt-6 bg-white rounded-lg p-5 border border-[#139191]/20" data-id="element-289">
                <div className="flex items-start gap-3" data-id="element-290">
                  <div className="h-8 w-8 rounded-lg bg-[#139191]/10 flex items-center justify-center flex-shrink-0" data-id="element-291">
                    <Sparkles className="h-4 w-4 text-[#139191]" data-id="element-292" />
                  </div>
                  <div data-id="element-293">
                    <h4 className="font-semibold text-gray-900 mb-2" data-id="element-294">
                      AI Analysis Summary
                    </h4>
                    <p className="text-sm text-gray-600 leading-relaxed" data-id="element-295">
                      The AI analyzed storytelling patterns, visual styles,
                      pacing techniques, and engagement hooks from these
                      swipefile videos. It identified the most effective
                      elements and combined them with your original idea to
                      create a unique concept that maintains authenticity while
                      leveraging proven content strategies.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-296">
            {/* Left Column - Context & Hook */}
            <div className="lg:col-span-1 space-y-6" data-id="element-297">
              {/* Context Section */}
              <Card className="border-gray-200 shadow-md" data-id="element-298">
                <CardHeader className="pb-4" data-id="element-299">
                  <div className="flex items-center gap-3" data-id="element-300">
                    <div className="h-10 w-10 rounded-lg bg-[#139191]/10 flex items-center justify-center" data-id="element-301">
                      <Lightbulb className="h-5 w-5 text-[#139191]" data-id="element-302" />
                    </div>
                    <CardTitle className="text-xl" data-id="element-303">Original Idea</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4" data-id="element-304">
                  <div data-id="element-305">
                    <h4 className="font-semibold text-gray-900 mb-2" data-id="element-306">
                      {concept.originalIdea.title}
                    </h4>
                    <p className="text-sm text-gray-600 leading-relaxed" data-id="element-307">
                      {concept.originalIdea.description}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-308">
                    {concept.originalIdea.tags.map(tag => <Badge key={tag} variant="outline" className="text-xs border-gray-200 bg-gray-50" data-id="element-309">
                        {tag}
                      </Badge>)}
                  </div>
                </CardContent>
              </Card>

              {/* Hook Section */}
              <Card className="border-gray-200 shadow-md" data-id="element-310">
                <CardHeader className="pb-4" data-id="element-311">
                  <div className="flex items-center gap-3" data-id="element-312">
                    <div className="h-10 w-10 rounded-lg bg-amber-500/10 flex items-center justify-center" data-id="element-313">
                      <Zap className="h-5 w-5 text-amber-600" data-id="element-314" />
                    </div>
                    <CardTitle className="text-xl" data-id="element-315">AI-Generated Hook</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4" data-id="element-316">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-317">
                    <p className="text-sm font-medium text-gray-900 leading-relaxed italic" data-id="element-318">
                      "{concept.selectedHook.text}"
                    </p>
                  </div>
                  <div data-id="element-319">
                    <Badge variant="outline" className="text-xs border-amber-200 bg-amber-50 text-amber-700" data-id="element-320">
                      {concept.selectedHook.tone}
                    </Badge>
                  </div>

                  <div className="pt-4 border-t border-gray-100" data-id="element-321">
                    <p className="text-xs font-semibold text-gray-700 mb-3 flex items-center gap-2" data-id="element-322">
                      <Sparkles className="h-3.5 w-3.5 text-[#139191]" data-id="element-323" />
                      Hook Inspired By:
                    </p>
                    <div className="space-y-2" data-id="element-324">
                      {concept.swipefileReferences.filter(ref => ref.usedFor.some(u => u.toLowerCase().includes('hook'))).map(ref => <div key={ref.id} className="flex items-start gap-2 bg-gray-50 rounded p-2" data-id="element-325">
                            <Video className="h-3.5 w-3.5 text-[#139191] mt-0.5 flex-shrink-0" data-id="element-326" />
                            <span className="text-xs text-gray-700" data-id="element-327">
                              {ref.title}
                            </span>
                          </div>)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Script & Visual Details */}
            <div className="lg:col-span-2 space-y-6" data-id="element-328">
              {/* Video Script */}
              <Card className="border-gray-200 shadow-md" data-id="element-329">
                <CardHeader className="pb-4" data-id="element-330">
                  <div className="flex items-center justify-between" data-id="element-331">
                    <div className="flex items-center gap-3" data-id="element-332">
                      <div className="h-10 w-10 rounded-lg bg-purple-500/10 flex items-center justify-center" data-id="element-333">
                        <FileText className="h-5 w-5 text-purple-600" data-id="element-334" />
                      </div>
                      <div data-id="element-335">
                        <CardTitle className="text-xl" data-id="element-336">Video Script</CardTitle>
                        <p className="text-sm text-gray-500 mt-1" data-id="element-337">
                          AI-generated • {concept.script.duration}
                        </p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" className="gap-2 border-gray-200" data-id="element-338">
                      <Copy className="h-4 w-4" data-id="element-339" />
                      Copy
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6" data-id="element-340">
                  {/* Intro */}
                  <div data-id="element-341">
                    <div className="flex items-center gap-2 mb-3" data-id="element-342">
                      <div className="h-6 w-6 rounded bg-[#139191] text-white text-xs font-bold flex items-center justify-center" data-id="element-343">
                        1
                      </div>
                      <h4 className="font-semibold text-gray-900" data-id="element-344">
                        Introduction
                      </h4>
                      <Badge variant="outline" className="text-xs border-gray-200 bg-gray-50" data-id="element-345">
                        0:00 - 0:10
                      </Badge>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 ml-8" data-id="element-346">
                      <p className="text-sm text-gray-700 leading-relaxed" data-id="element-347">
                        {concept.script.intro}
                      </p>
                    </div>
                  </div>

                  {/* Body */}
                  {concept.script.body.map((section, idx) => <div key={idx} data-id="element-348">
                      <div className="flex items-center gap-2 mb-3" data-id="element-349">
                        <div className="h-6 w-6 rounded bg-[#139191] text-white text-xs font-bold flex items-center justify-center" data-id="element-350">
                          {idx + 2}
                        </div>
                        <h4 className="font-semibold text-gray-900" data-id="element-351">
                          Main Point {idx + 1}
                        </h4>
                        <Badge variant="outline" className="text-xs border-gray-200 bg-gray-50" data-id="element-352">
                          0:{10 + idx * 15} - 0:{25 + idx * 15}
                        </Badge>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 ml-8" data-id="element-353">
                        <p className="text-sm text-gray-700 leading-relaxed" data-id="element-354">
                          {section}
                        </p>
                      </div>
                    </div>)}

                  {/* Outro */}
                  <div data-id="element-355">
                    <div className="flex items-center gap-2 mb-3" data-id="element-356">
                      <div className="h-6 w-6 rounded bg-[#139191] text-white text-xs font-bold flex items-center justify-center" data-id="element-357">
                        {concept.script.body.length + 2}
                      </div>
                      <h4 className="font-semibold text-gray-900" data-id="element-358">
                        Conclusion & CTA
                      </h4>
                      <Badge variant="outline" className="text-xs border-gray-200 bg-gray-50" data-id="element-359">
                        0:{40 + concept.script.body.length * 15} - End
                      </Badge>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 ml-8" data-id="element-360">
                      <p className="text-sm text-gray-700 leading-relaxed" data-id="element-361">
                        {concept.script.outro}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Visual Style Guide */}
              <Card className="border-gray-200 shadow-md" data-id="element-362">
                <CardHeader className="pb-4" data-id="element-363">
                  <div className="flex items-center gap-3" data-id="element-364">
                    <div className="h-10 w-10 rounded-lg bg-pink-500/10 flex items-center justify-center" data-id="element-365">
                      <Image className="h-5 w-5 text-pink-600" data-id="element-366" />
                    </div>
                    <div data-id="element-367">
                      <CardTitle className="text-xl" data-id="element-368">
                        Visual Style Guide
                      </CardTitle>
                      <p className="text-sm text-gray-500 mt-1" data-id="element-369">
                        AI-generated from swipefile references
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6" data-id="element-370">
                  {/* Style */}
                  <div data-id="element-371">
                    <h4 className="font-semibold text-gray-900 mb-2" data-id="element-372">
                      Visual Style
                    </h4>
                    <p className="text-sm text-gray-600" data-id="element-373">
                      {concept.visualReference.style}
                    </p>
                  </div>

                  {/* Color Palette */}
                  <div data-id="element-374">
                    <h4 className="font-semibold text-gray-900 mb-3" data-id="element-375">
                      Color Palette
                    </h4>
                    <div className="flex gap-3" data-id="element-376">
                      {concept.visualReference.colorPalette.map((color, idx) => <div key={idx} className="flex flex-col items-center gap-2" data-id="element-377">
                            <div className="h-16 w-16 rounded-lg shadow-md border border-gray-200" style={{
                        backgroundColor: color
                      }} data-id="element-378" />
                            <span className="text-xs font-mono text-gray-600" data-id="element-379">
                              {color}
                            </span>
                          </div>)}
                    </div>
                  </div>

                  {/* Shot List */}
                  <div data-id="element-380">
                    <h4 className="font-semibold text-gray-900 mb-3" data-id="element-381">
                      Shot List
                    </h4>
                    <div className="space-y-3" data-id="element-382">
                      {concept.visualReference.shots.map((shot, idx) => <div key={idx} className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-383">
                          <div className="flex items-start gap-3 mb-2" data-id="element-384">
                            <Badge variant="outline" className="text-xs border-gray-300 bg-white shrink-0" data-id="element-385">
                              {shot.timestamp}
                            </Badge>
                            <div className="flex-1" data-id="element-386">
                              <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-387">
                                {shot.description}
                              </p>
                              <p className="text-xs text-gray-600" data-id="element-388">
                                {shot.notes}
                              </p>
                            </div>
                          </div>
                          {shot.inspiredBy && <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200" data-id="element-389">
                              <Sparkles className="h-3 w-3 text-[#139191]" data-id="element-390" />
                              <span className="text-xs text-gray-600" data-id="element-391">
                                Inspired by:{' '}
                                <span className="font-medium text-[#139191]" data-id="element-392">
                                  {shot.inspiredBy}
                                </span>
                              </span>
                            </div>}
                        </div>)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>;
}